import { useForm, FormProvider, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import BaseInput from "../../../shared/components/BaseInput";
import BaseButton from "components/common/BaseButton";
import { useIntl } from "react-intl";
import { useNAMStates } from "pages/admin-app/states/hooks/useNAMStates";
import {
  DistributorFormData,
  getDistributorFormSchema,
} from "./create-distributor.schema";
import { useCountries } from "pages/admin-app/country/hooks/useCountries";
import { loadStates } from "pages/admin-app/states/services/loadStates";
import { loadCountries } from "pages/admin-app/country/services/loadCountries";
import BaseAsyncPaginate from "pages/admin-app/shared/components/BaseAsyncPaginate";
import { useEffect } from "react";

interface Props {
  onSubmit: (data: DistributorFormData) => void;
  isSubmitting: boolean;
}

const CreateDistributorForm = ({ onSubmit, isSubmitting }: Props) => {
  const intl = useIntl();
  const distributorFormSchema = getDistributorFormSchema(intl);

  const methods = useForm({
    resolver: zodResolver(distributorFormSchema),
  });

  const { handleSubmit, control, setValue, clearErrors } = methods;
  const selectedCountry = useWatch({ control, name: "countryId" });

  useEffect(() => {
    setValue("state", "");
    clearErrors("state");
  }, [selectedCountry, setValue, clearErrors]);

  const {
    data: states,
    isLoading: isLoadingStates,
    error: statesError,
    isError: isErrorStates,
    refetch: refetchStates,
    isFetching: isFetchingStates,
  } = useNAMStates({
    countryId: selectedCountry,
    currentPage: 1,
  });

  const {
    data: countries,
    isLoading: isLoadingCountries,
    isError: isErrorCountries,
    error: countriesError,
    refetch: refetchCountries,
    isFetching: isFetchingCountries,
  } = useCountries({ currentPage: 1 });

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="inputs-container">
        <BaseInput
          name="name"
          label={intl.formatMessage({ id: "usAdmin.name" })}
          type="text"
          containerClassName="usadmin-input-name"
        />
        <BaseInput
          name="contactName"
          label={intl.formatMessage({ id: "usAdmin.contactName.distributor" })}
          type="text"
          containerClassName="usadmin-input-contactName"
        />
        <BaseInput
          name="address"
          label={intl.formatMessage({ id: "usAdmin.address" })}
          type="text"
          containerClassName="second-column"
        />
        <div className="third-row">
          <BaseInput
            name="email"
            label={intl.formatMessage({ id: "usAdmin.email.distributor" })}
            type="text"
          />
          <BaseInput
            name="phone"
            label={intl.formatMessage({ id: "usAdmin.phone" })}
            type="text"
          />
          <BaseInput
            name="zip"
            label={intl.formatMessage({ id: "usAdmin.zip" })}
            type="text"
          />
        </div>
        <div className="third-row">
          <BaseInput
            name="city"
            label={intl.formatMessage({ id: "usAdmin.city" })}
            type="text"
          />
          <BaseAsyncPaginate
            name="state"
            label={intl.formatMessage({ id: "usAdmin.state" })}
            loadOptions={loadStates(selectedCountry)}
            data={states?.data || []}
            isLoading={isLoadingStates || isFetchingStates}
            isError={isErrorStates}
            error={statesError}
            refetch={refetchStates}
            optionMapper={(state) => ({
              value: state.abbr,
              label: `${state.name} (${state.abbr})`,
            })}
            placeholder={intl.formatMessage({ id: "usAdmin.selectState" })}
            isDisabled={!selectedCountry}
            selectedCountry={selectedCountry}
          />
          <BaseAsyncPaginate
            name="countryId"
            label={intl.formatMessage({ id: "country" })}
            loadOptions={loadCountries}
            data={countries?.data || []}
            additional={{ page: 1 }}
            isLoading={isLoadingCountries || isFetchingCountries}
            isError={isErrorCountries}
            error={countriesError}
            optionMapper={(country) => ({
              value: country.id,
              label: country.name,
            })}
            refetch={refetchCountries}
            placeholder={intl.formatMessage({ id: "usAdmin.selectCountry" })}
          />
        </div>
        <BaseButton
          text={intl.formatMessage({ id: "usAdmin.create" })}
          type="submit"
          disabled={isSubmitting}
          className="first-column usadmin-form-submit-button"
          handleSubmit={() => {}}
        />
      </form>
    </FormProvider>
  );
};

export default CreateDistributorForm;
